import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  CreditCard,
  Building2,
  Calendar,
  DollarSign,
  Receipt,
  Tag,
  Clock,
  Network,
  Coins,
  Percent,
  ExternalLink,
  Ticket,
  History,
  Info,
  ArrowLeftRight,
  Layers,
} from "lucide-react";
import { format } from "date-fns";

interface TransactionDetailsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: any;
  originalItem?: any;
}

export const TransactionDetailsModal: React.FC<TransactionDetailsProps> = ({
  open,
  onOpenChange,
  item,
  originalItem,
}) => {
  if (!item) return null;

  // For debugging, log the original item
  if (originalItem) {
    console.log("Original API object:", originalItem);
  }

  const statusStyles = {
    completed: "bg-green-100 text-green-800 hover:bg-green-100",
    pending: "bg-yellow-100 text-yellow-800 hover:bg-yellow-100",
    failed: "bg-red-100 text-red-800 hover:bg-red-100",
  };

  const InfoItem = ({
    icon,
    label,
    value,
  }: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
  }) => (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      <span className="font-medium text-sm">{value}</span>
    </div>
  );

  console.log(item, "item---");
  // <CreditCard className="h-5 w-5" />

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0">
        <DialogHeader className="p-3 bg-gray-50 border-b">
          <DialogTitle className="text-lg font-semibold">
            Transaction Details
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh]">
          <div className="p-6 space-y-8">
            {/* Xpand Point Transfer Details Section */}
            {originalItem ? (
              <>
                {/* Program(From) Details */}
                <div>
                  <h3 className="text-base font-semibold mb-3 flex items-center">
                    <ArrowLeftRight className="h-5 w-5 mr-2" />
                    Program(From) Details
                  </h3>
                  <div className="flex justify-between">
                    <div>
                      <span className="text-xs text-gray-500">Program:</span>
                      <div className="font-medium">
                        {item.partnerName ?? "-"}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Program ID:</span>
                      <div className="font-medium">
                        {originalItem?.transferPartnerVO?.id ?? "-"}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Member ID:</span>
                      <div className="font-medium">
                        {item.transferPartnerMemberId ?? "-"}
                      </div>
                    </div>
                  </div>
                </div>
                <Separator />
                {/* Transfer Partner(To) Details */}
                <div>
                  <h3 className="text-base font-semibold mb-3 flex items-center">
                    <Layers className="h-5 w-5 mr-2" />
                    Transfer Partner(To) Details
                  </h3>
                  <div className="flex justify-between">
                    <div>
                      <span className="text-xs text-gray-500">Partner:</span>
                      <div className="font-medium">{item.program ?? "-"}</div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Partner ID:</span>
                      <div className="font-medium">
                        {originalItem?.programVO?.id ?? "-"}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Member ID:</span>
                      <div className="font-medium">
                        {item.programMemberId ?? "-"}
                      </div>
                    </div>
                  </div>
                </div>
                <Separator />
                {/* Transaction History */}
                <div>
                  <h3 className="text-base font-semibold mb-3 flex items-center">
                    <History className="h-5 w-5 mr-2" />
                    Transaction History
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-gray-500">Date:</span>
                      <div className="font-medium">{item.dateTime ?? "-"}</div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">
                        Last Updated:
                      </span>
                      <div className="font-medium">-</div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">
                        Transaction Status:
                      </span>
                      {/* const statusStyles = {
                    accrual_success:
                      "bg-green-100 text-green-800 hover:bg-green-100",
                    pending:
                      "bg-yellow-100 text-yellow-800 hover:bg-yellow-100",
                    failed: "bg-red-100 text-red-800 hover:bg-red-100",
                  };

                  const statusLabels = {
                    accrual_success: "Accrual Success",
                    pending: "Pending",
                    failed: "Failed",
                  }; */}
                      <div className="font-medium mt-3">
                        {item.status === "accrual_success" ? (
                          <span className="bg-green-100 text-green-800 hover:bg-green-100 p-2">
                            Accrual Success
                          </span>
                        ) : item.status === "redemption_success" ? (
                          <span className="bg-blue-100 text-blue-800 hover:bg-blue-100 p-2">
                            Redemption Success
                          </span>
                        ) : item.status === "pending" ? (
                          <span className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 p-2">
                            Pending
                          </span>
                        ) : item.status === "failed" ? (
                          <span className="bg-red-100 text-red-800 hover:bg-red-100 p-2">
                            Accrual Failed
                          </span>
                        ) : (
                          "-"
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <Separator />
                {/* Program Finance */}
                <div>
                  <h3 className="text-base font-semibold mb-3 flex items-center">
                    <DollarSign className="h-5 w-5 mr-2" />
                    Program Finance
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-gray-500">
                        Program Currency:
                      </span>
                      <div className="font-medium">
                        {
                          originalItem.transferPartnerCurrencyVO
                            .iso3CurrencyCode
                        }
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">
                        Points Redeemed:
                      </span>
                      <div className="font-medium">
                        {item.pointsRedeemed ?? "-"}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">
                        Redemption CPP:
                      </span>
                      <div className="font-medium">
                        {originalItem.transferPartnerCpp}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">
                        Redemption Value:
                      </span>
                      <div className="font-medium">
                        {/* {item.redemptionAmount ?? "-"} */}
                        {originalItem?.transferPartnerPointsAmount}{" "}
                        {
                          originalItem?.transferPartnerCurrencyVO
                            ?.iso3CurrencyCode
                        }
                      </div>
                    </div>
                  </div>
                </div>
                <Separator />
                {/* Partner Finance */}
                <div>
                  <h3 className="text-base font-semibold mb-3 flex items-center">
                    <Coins className="h-5 w-5 mr-2" />
                    Partner Finance
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-gray-500">
                        Loyyal Profit %:
                      </span>
                      <div className="font-medium">
                        {originalItem?.loyyalProfitPercentage}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">
                        Accrual CPP:
                      </span>
                      <div className="font-medium">
                        {originalItem?.programCpp}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">
                        Value Accrued in Partner Currency:
                      </span>
                      <div className="font-medium">
                        {originalItem?.transferPartnerPointsAmount}{" "}
                        {
                          originalItem?.transferPartnerCurrencyVO
                            ?.iso3CurrencyCode
                        }
                      </div>
                    </div>
                    {/* <div>
                      <span className="text-xs text-gray-500">
                        Exchange Rate:
                      </span>
                      <div className="font-medium">-</div>
                    </div> */}
                    <div>
                      <span className="text-xs text-gray-500">
                        Value Accrued in Program Currency:
                      </span>
                      <div className="font-medium">
                        {item.accruedAmount
                          ? `${item.accruedAmount} ${item.accruedCurrency}`
                          : "-"}
                      </div>
                    </div>
                  </div>
                </div>
                <Separator />
              </>
            ) : (
              <div>
                <div className="mb-3">
                  <h3 className="text-base font-semibold mb-3 flex item-center">
                    <CreditCard className="h-5 w-5 mr-2" />
                    Transaction Details
                  </h3>
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <span className="text-xs text-gray-500">
                        Loyyal Transaction ID:
                      </span>
                      <div className="font-medium text-sm">
                        {item.id ?? "-"}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Program:</span>
                      <div className="font-medium text-sm">
                        {item.programVO?.programName ?? "-"}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">
                        Earn Status:
                      </span>
                      {/* bg-green-100 text-green-800 */}
                      <div className="font-medium text-sm">
                        {item.accrualStatus === "ACCRUAL_SUCCESSFUL" ? (
                          <div className="bg-green-100 text-green-800 p-1.5 w-fit rounded-md text-xs">
                            Earned
                          </div>
                        ) : item.accrualStatus === "ACCRUAL_FAILED" ? (
                          <div className="bg-red-100 text-red-800 p-1.5 w-fit rounded-md text-xs">
                            Failed
                          </div>
                        ) : item.accrualStatus === "NOT_ACCRUED" ? (
                          <div className="bg-gray-200 text-gray-800 p-1.5 w-fit rounded-md text-xs">
                            Not Accrued
                          </div>
                        ) : item.accrualStatus === "ACCURAL_IN_PROGRESS" ? (
                          <div className="bg-yellow-100 text-yellow-800 p-1.5 w-fit rounded-md text-xs">
                            Pending
                          </div>
                        ) : (
                          "-"
                        )}
                      </div>
                    </div>
                    {/* <div>
                      <span className="text-xs text-gray-500">
                        Accrual Transaction Id:
                      </span>
                      <div className="font-medium">
                       
                      </div>
                    </div> */}
                  </div>
                </div>
                <Separator />
                {/* Merchant Information Section */}
                <div className="mb-3">
                  <h3 className="text-base font-semibold mb-3 mt-6 flex items-center">
                    <Building2 className="h-5 w-5 mr-2" />
                    Merchant Information
                  </h3>
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <span className="text-xs text-gray-500">
                        Merchant Name:
                      </span>
                      <div className="font-medium text-sm">
                        {item.merchantVO?.merchantName ?? "-"}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">
                        Merchant Code:
                      </span>
                      <div className="font-medium text-sm">
                        {" "}
                        {item.merchantVO?.merchantCode ?? "-"}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">
                        Merchant Type:
                      </span>
                      <div className="font-medium text-sm">
                        {item.merchantVO?.businessType ?? "-"}
                      </div>
                    </div>
                  </div>
                </div>
                <Separator />
                {/* Financial Details Section */}
                <div className="mb-3">
                  <h3 className="text-base font-semibold mb-3 mt-6 flex item-center">
                    <DollarSign className="h-5 w-5" />
                    Financial Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-gray-500">Amount:</span>
                      <div className="font-medium">
                        {item.transactionAmount ?? "-"}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Currency:</span>
                      <div className="font-medium">
                        {item.transactionCurrency ?? "-"}
                      </div>
                    </div>
                    {/* <div>
                      <span className="text-xs text-gray-500">
                        Original Currency Amount:
                      </span>
                      <div className="font-medium">
                       
                      </div>
                    </div> */}
                    {/* <div>
                      <span className="text-xs text-gray-500">
                        Original Currency:
                      </span>
                      <div className="font-medium">
                       
                      </div>
                    </div> */}
                  </div>
                </div>
                <Separator />
                {/* Cashback Details Section */}
                <div className="mb-3">
                  <h3 className="text-base font-semibold mb-3 mt-6 flex items-center">
                    <Coins className="h-5 w-5 mr-2" />
                    Cashback Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-gray-500">
                        Cashback Amount:
                      </span>
                      <div className="font-medium">
                        {item.transactionCashbackAmount ?? "-"}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">
                        Cashback Percentage:
                      </span>
                      <div className="font-medium">
                        {item.transactionCashbackPercentage ?? "-"}
                      </div>
                    </div>
                  </div>
                </div>
                <Separator />
                {/* Merchant Transaction History Section */}
                <div className="mb-3">
                  <h3 className="text-base font-semibold mb-3 mt-6 flex items-center">
                    <History className="h-5 w-5 mr-2 mr-2" />
                    Merchant Transaction History
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-gray-500">Date:</span>
                      <div className="font-medium">
                        {item.transactionDate ? (
                          <span>
                            {new Date(item.transactionDate).toLocaleString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              }
                            )}
                          </span>
                        ) : (
                          "-"
                        )}
                      </div>
                    </div>
                    {/* <div>
                      <span className="text-xs text-gray-500">
                        Last Updated:
                      </span>
                      <div className="font-medium">
                        

                        {item.merchantVO?.lastUpdatedDateInMillis
                          ? new Date(
                              item.merchantVO.lastUpdatedDateInMillis
                            ).toLocaleString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            })
                          : "-"}

                        
                       
                      </div>
                    </div> */}
                    <div>
                      <span className="text-xs text-gray-500">
                        Merchant Transaction ID:
                      </span>
                      <div className="font-medium">
                        {item.merchantTransactionId ?? "-"}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">
                        Transaction Status:
                      </span>
                      <div className="font-medium">
                        {item.transactionStatus === 0 ? (
                          <div className="bg-gray-200 text-gray-800 p-2 w-fit rounded-md">
                            Not Finalized
                          </div>
                        ) : item.transactionStatus === 1 ? (
                          <div className="bg-blue-100 text-blue-800 p-2 w-fit rounded-md">
                            Finalized
                          </div>
                        ) : item.transactionStatus === 2 ? (
                          <div className="bg-purple-100 text-purple-800 p-2 w-fit rounded-md">
                            Refunded
                          </div>
                        ) : (
                          "-"
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <Separator />
                {/* Member Details Section */}
                <div>
                  <h3 className="text-base font-semibold mb-3 mt-6 flex items-center">
                    <Info className="h-5 w-5 mr-2 mr-2 mr-2" />
                    Member Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-gray-500">Member ID:</span>
                      <div className="font-medium">
                        {item.memberVO?.programMemberId ?? "-"}
                      </div>
                    </div>
                    {/* <div>
                      <span className="text-xs text-gray-500">
                        Member Name:
                      </span>
                      <div className="font-medium">
                       
                      </div>
                    </div> */}
                    {/* <div>
                      <span className="text-xs text-gray-500">
                        Member Email:
                      </span>
                      <div className="font-medium">
                       
                      </div>
                    </div> */}
                  </div>
                </div>
              </div>
            )}
            {/* Transaction Details Section */}
          </div>
        </ScrollArea>
        <DialogFooter className="p-3 border-t bg-gray-50">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
